import React, { useState, useEffect } from 'react';
// [Web UX 3. csomag (D), 2026-10-04] A kérés-hibák szövege a közös `apiErrorMessage`-en át megy (a szerver magyar
// indoklása mindhárom törzs-alakból / hálózati hibánál magyar mondat / különben „<tartalék> (HTTP n)"),
// a nyers `err.response?.data?.message` helyett. Őr: `utils/__tests__/rawErrorMessages.guard.test.ts`.
import { apiErrorMessage } from '../../utils/apiErrors';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import {
  Fab,
} from '@mui/material';
import { CalendarEvent, EventTypeCalendar } from '../../types/calendar';
import { buildDailyGrid, layoutDailyEvents, SLOT_HEIGHT } from './dailyTimelineLayout';
import { calendarService } from '../../services/calendarService';
import { formatDateToLocal, getTodayLocal } from '../../utils/dateUtils';
import EventDetailsDialog from './EventDetailsDialog';
import RegisteredMarker, { showRegisteredMarker } from './RegisteredMarker';
import EventCreateDialog from './EventCreateDialog';
import EventEditDialog from './EventEditDialog';
import { useAuth } from '../../contexts/AuthContext';
import { useResponsive } from '../../hooks/useResponsive';
import { NAV_BUTTON_TOUCH_SX, buttonKeyHandlers, cardFocusRingSx, eventCardAriaLabel } from './calendarA11y';

interface DailyTimelineProps {
  selectedDate: string;
  onDateChange?: (date: string) => void;
  venueFilter?: string;
  availableOnly?: boolean;
  /** Elrejti a betelt eseményeket, amelyeknek a felhasználó nem résztvevője. */
  excludeFullOthers?: boolean;
  eventTypeFilter?: string[];
  coachFilter?: string[];
  refreshTrigger?: number;
  onNavigateToUserProfile?: (userId: string) => void;
  /** Az esemény ablakából a jelenléti ívre visz, ARRA az eseményre. */
  onNavigateToAttendance?: (eventId: string, eventDate: string) => void;
}

/** A napi kártya létszám-sora — a látható szöveg ÉS az akadálymentes név is ebből jön [Web UX 9 / T5]. */
const participantsText = (event: CalendarEvent): string =>
  event.maxParticipants
    ? `${event.participantCount}/${event.maxParticipants} fő`
    : `${event.participantCount} résztvevő`;

const DailyTimeline: React.FC<DailyTimelineProps> = ({
  selectedDate,
  onDateChange,
  venueFilter,
  availableOnly,
  excludeFullOthers,
  eventTypeFilter,
  coachFilter,
  refreshTrigger,
  onNavigateToUserProfile,
  onNavigateToAttendance
}) => {
  const { user } = useAuth();
  const { isMobile } = useResponsive();
  const [currentDate, setCurrentDate] = useState(new Date(selectedDate));
  const [dailyEvents, setDailyEvents] = useState<CalendarEvent[]>([]);
  const [eventTypes, setEventTypes] = useState<EventTypeCalendar[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  
  // Dialog states
  const [eventDetailsOpen, setEventDetailsOpen] = useState(false);
  const [eventCreateOpen, setEventCreateOpen] = useState(false);
  const [eventEditOpen, setEventEditOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [createEventTime, setCreateEventTime] = useState<{start: string, end: string}>({start: '', end: ''});

  // Check if user can create events (not Player role)
  const canCreateEvents = user && user.userRole !== 'Player';

  /**
   * A rács sorai és az események PERCPONTOS helye. A régi megoldás csak az exakt egész/fél
   * órás kezdést rajzolta ki, és a „lefedett" sávban induló eseményt eldobta — így pl. a
   * 09:15-es kezdésű esemény a napi nézetben LÁTHATATLAN volt. A számolás a
   * `dailyTimelineLayout.ts`-ben van, mert ott render nélkül tesztelhető.
   */
  const grid = buildDailyGrid(dailyEvents);
  const positionedEvents = layoutDailyEvents(dailyEvents, grid);
  /** A bal oldali idő-oszlop teljes szélessége (80px szöveg + 16px térköz). */
  const TIME_COLUMN_WIDTH = 96;

  useEffect(() => {
    const newDate = new Date(selectedDate);
    setCurrentDate(newDate);
    loadDailyEvents();
  }, [selectedDate, venueFilter, availableOnly, excludeFullOthers, eventTypeFilter, coachFilter, refreshTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadEventTypes();
  }, []);

  const loadDailyEvents = async () => {
    try {
      setLoading(true);
      setError('');

      console.log('DailyTimeline: Loading events for', selectedDate, 'with filters:', {
        venueFilter,
        availableOnly,
        excludeFullOthers,
        eventTypeFilter,
        coachFilter
      });

      // Filter events on client side since getCalendarEvents doesn't support multiple filters yet
      const query = {
        startDate: selectedDate,
        endDate: selectedDate,
        venueId: venueFilter || undefined,
        availableOnly: availableOnly || false,
        excludeFullOthers: excludeFullOthers || false,
        coachIds: coachFilter && coachFilter.length > 0 ? coachFilter : undefined,
      };

      let events = await calendarService.getCalendarEvents(query);

      // Apply event type filter on client side if provided
      if (eventTypeFilter && eventTypeFilter.length > 0) {
        events = events.filter(event =>
          eventTypeFilter.some(typeId => event.eventTypeId === typeId)
        );
      }

      console.log('DailyTimeline: Loaded events:', events);
      setDailyEvents(events);
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Hiba történt az események betöltésekor'));
    } finally {
      setLoading(false);
    }
  };


  const loadEventTypes = async () => {
    try {
      const eventTypeData = await calendarService.getActiveEventTypes();
      setEventTypes(eventTypeData);
    } catch (err) {
      console.error('Error loading event types:', err);
    }
  };

  const navigateDay = (direction: 'prev' | 'next') => {
    setCurrentDate(prevDate => {
      const newDate = new Date(prevDate);
      if (direction === 'prev') {
        newDate.setDate(newDate.getDate() - 1);
      } else {
        newDate.setDate(newDate.getDate() + 1);
      }
      const dateStr = formatDateToLocal(newDate);
      onDateChange?.(dateStr);
      return newDate;
    });
  };

  const goToToday = () => {
    const today = new Date();
    const dateStr = getTodayLocal();
    setCurrentDate(today);
    onDateChange?.(dateStr);
  };

  const formatDate = (date: Date): string => {
    const hungaryDayNames = ['Vasárnap', 'Hétfő', 'Kedd', 'Szerda', 'Csütörtök', 'Péntek', 'Szombat'];
    const hungaryMonthNames = [
      'január', 'február', 'március', 'április', 'május', 'június',
      'július', 'augusztus', 'szeptember', 'október', 'november', 'december'
    ];
    
    const dayName = hungaryDayNames[date.getDay()];
    const monthName = hungaryMonthNames[date.getMonth()];
    
    return `${date.getFullYear()}. ${monthName} ${date.getDate()}. (${dayName})`;
  };

  const isToday = (): boolean => {
    const today = new Date();
    return currentDate.toDateString() === today.toDateString();
  };

  const formatTime = (timeString: string): string => {
    return timeString.substring(0, 5);
  };

  const handleEventClick = (eventId: string) => {
    setSelectedEventId(eventId);
    setEventDetailsOpen(true);
  };

  const handleCreateEventClick = (startTime?: string) => {
    if (startTime) {
      const startHour = parseInt(startTime.split(':')[0], 10);
      // MÁSODIK VÉDŐVONAL (2026-09-13): a `24:00` sor kattintása korábban `24:00:00` kezdéssel
      // nyitotta a létrehozót, amit a szerver 400-cal dobott vissza (a `TimeOnly` csak
      // 00:00–23:59 között értelmes), ráadásul a vég 23:59 lett, tehát vég < kezdés. A rács ma
      // már nem is állít elő ilyen sort (`buildDailyGrid` a nap végénél megáll), de ha bármi
      // mégis érvénytelen órát adna, itt megállunk — érvénytelen esemény-tervet nem nyitunk.
      if (!Number.isFinite(startHour) || startHour < 0 || startHour > 23) {
        return;
      }
      const start = startTime.padEnd(8, ':00');
      const endHour = startHour + 1;
      const endMinute = startTime.split(':')[1];
      // A rács a késő esti eseményekhez kinyílhat (pl. 23:30-as sor); onnan a „+1 óra"
      // 24:30 lenne, ami érvénytelen idő — ilyenkor a nap végére vágjuk.
      const end = endHour > 23
        ? '23:59:00'
        : `${endHour.toString().padStart(2, '0')}:${endMinute}:00`;
      setCreateEventTime({ start, end });
    } else {
      setCreateEventTime({ start: '', end: '' });
    }
    setEventCreateOpen(true);
  };

  const handleEditEvent = (eventId: string) => {
    setSelectedEventId(eventId);
    setEventDetailsOpen(false);
    setEventEditOpen(true);
  };

  const handleDeleteEvent = (eventId: string) => {
    setEventDetailsOpen(false);
    loadDailyEvents(); // Reload calendar data
  };

  const handleEventUpdated = () => {
    loadDailyEvents(); // Reload calendar data
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Daily Timeline Header */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          mb: 2,
          flexWrap: 'wrap',
          gap: 1
        }}>
          {/* [Web UX 9 / T5, 2026-10-13] címke nélküli lapozók (`DailyTimeline.tsx:267,285`) → aria-label +
              telefonon 44 px. Őr: `utils/__tests__/iconButtonLabels.guard.test.ts`. */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
            <IconButton onClick={() => navigateDay('prev')} size="small" aria-label="Előző nap" sx={NAV_BUTTON_TOUCH_SX}>
              <ChevronLeftIcon />
            </IconButton>
            <Typography
              variant="h5"
              sx={{
                minWidth: { xs: '140px', sm: 300 },
                maxWidth: { xs: '160px', sm: 'none' },
                textAlign: 'center',
                fontSize: { xs: '0.95rem', sm: '1.5rem' },
                fontWeight: 'medium',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {formatDate(currentDate)}
            </Typography>
            <IconButton onClick={() => navigateDay('next')} size="small" aria-label="Következő nap" sx={NAV_BUTTON_TOUCH_SX}>
              <ChevronRightIcon />
            </IconButton>
            <Tooltip title="Mai nap">
              <IconButton
                onClick={goToToday}
                color={isToday() ? 'primary' : 'default'}
                size="small"
                aria-label="Mai nap"
                sx={NAV_BUTTON_TOUCH_SX}
              >
                <TodayIcon />
              </IconButton>
            </Tooltip>
          </Box>

        </Box>

        {/* Event Summary */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Összesen {dailyEvents.length} esemény
          </Typography>
          {dailyEvents.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {eventTypes
                .filter(type => dailyEvents.some(event => event.eventTypeDisplayName === type.displayName))
                .map(type => (
                  <Chip
                    key={type.eventTypeId}
                    label={type.displayName}
                    size="small"
                    sx={{ backgroundColor: type.color, color: 'white' }}
                  />
                ))}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Timeline */}
      <Paper sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : dailyEvents.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <Typography variant="h6" color="text.secondary">
              Nincs esemény ezen a napon
            </Typography>
          </Box>
        ) : (
          <Box sx={{ position: 'relative' }}>
            {/* Idő-rács: fél órás sorok. Az eseményeket NEM ezek a sorok tartalmazzák — a
                kártyák percpontos helyre, a rács FÖLÉ kerülnek, így egyetlen esemény sem
                eshet ki azért, mert a kezdése nem fél órára esik, vagy mert egy hosszabb
                esemény „lefedi" a sávját. */}
            {grid.slots.map((slot) => (
              <Box
                key={slot.displayTime}
                sx={{ display: 'flex', alignItems: 'flex-start', height: SLOT_HEIGHT, boxSizing: 'border-box' }}
              >
                {/* Time column */}
                <Box sx={{ width: 80, pt: 1, pr: 2, flexShrink: 0 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontFamily: 'monospace' }}
                  >
                    {slot.displayTime}
                  </Typography>
                </Box>

                {/* Üres sáv — kattintásra új esemény (ahol kártya van, a kártya fogja a kattintást) */}
                <Box
                  sx={{
                    flex: 1,
                    height: '100%',
                    cursor: canCreateEvents ? 'pointer' : 'default',
                    borderRadius: 1,
                    '&:hover': { backgroundColor: canCreateEvents ? 'action.hover' : 'transparent' },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.secondary',
                  }}
                  onClick={canCreateEvents ? () => handleCreateEventClick(slot.displayTime) : undefined}
                  title={canCreateEvents ? "Kattintson új esemény létrehozásához" : "Nincs jogosultság esemény létrehozására"}
                >
                  {slot.minute === 0 && (
                    <Divider sx={{ opacity: 0.3, width: '100%' }} />
                  )}
                </Box>
              </Box>
            ))}

            {/* Esemény-kártyák: percpontos pozíció; az átfedők egymás mellé, hasábokba kerülnek */}
            {positionedEvents.map(({ event, top, height, columnIndex, columnCount }) => {
              const columnWidth = `calc((100% - ${TIME_COLUMN_WIDTH}px) / ${columnCount})`;
              const compact = columnCount > 1 || height < 80;

              return (
                // [Web UX 9 / T5, 2026-10-13] A napi kártya billentyűzetről elérhetetlen volt (`onClick`-es Card,
                // `DailyTimeline.tsx:386`, UX-lista 6.2): `role="button"` + `tabIndex` + Enter/Szóköz, látható
                // fókusz-gyűrű; az akadálymentes név a kártyán látható adatok („Jelentkeztél: " előtaggal).
                <Card
                  key={event.eventId}
                  role="button"
                  tabIndex={0}
                  aria-label={eventCardAriaLabel({
                    eventName: event.eventName,
                    startTime: event.startTime,
                    endTime: event.endTime,
                    venueName: event.venueName,
                    extra: [
                      event.eventTypeDisplayName,
                      event.eventTypeDisplayName !== 'Asztalfoglalás' && participantsText(event),
                    ],
                    registered: showRegisteredMarker(event),
                  })}
                  onClick={() => handleEventClick(event.eventId)}
                  {...buttonKeyHandlers(() => handleEventClick(event.eventId))}
                  sx={{
                    backgroundColor: event.eventTypeColor + '20',
                    border: `2px solid ${event.eventTypeColor}`,
                    borderLeft: `6px solid ${event.eventTypeColor}`,
                    cursor: 'pointer',
                    position: 'absolute',
                    top: `${top}px`,
                    left: `calc(${TIME_COLUMN_WIDTH}px + (${columnWidth}) * ${columnIndex})`,
                    width: `calc(${columnWidth} - 4px)`,
                    height: `${height}px`,
                    zIndex: 1,
                    '&:hover': {
                      boxShadow: 2,
                      zIndex: 2,
                    },
                    // a fókuszált kártya (gyűrűvel) a szomszédos, átfedő kártyák fölé kerül
                    '&:focus-visible': { ...cardFocusRingSx['&:focus-visible'], zIndex: 3 },
                    transition: 'box-shadow 0.2s ease-in-out',
                  }}
                >
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, height: '100%', overflow: 'hidden' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: compact ? '0.75rem' : '1rem' }}>
                        {event.eventName}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {/* [Web UX 1. csomag, 2026-10-03] „Jelentkeztél" a típus-címke mellett
                            (közös jelző; a függő állapot előzi — ez a nézet azt ma nem jelzi külön). */}
                        {showRegisteredMarker(event) && <RegisteredMarker size={compact ? 'tiny' : 'small'} />}
                        <Chip
                          label={event.eventTypeDisplayName}
                          size="small"
                          sx={{
                            backgroundColor: event.eventTypeColor,
                            color: 'white',
                            fontSize: compact ? '0.65rem' : '0.75rem',
                            height: compact ? 20 : 24,
                          }}
                        />
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5, flexWrap: 'wrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TimeIcon fontSize="small" color="action" />
                        <Typography variant="body2" sx={{ fontSize: compact ? '0.7rem' : '0.875rem' }}>
                          {formatTime(event.startTime)} - {formatTime(event.endTime)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LocationIcon fontSize="small" color="action" />
                        <Typography variant="body2" sx={{ fontSize: compact ? '0.7rem' : '0.875rem' }}>
                          {event.venueName}
                        </Typography>
                      </Box>
                    </Box>

                    {event.eventTypeDisplayName !== 'Asztalfoglalás' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PeopleIcon fontSize="small" color="action" />
                      <Typography variant="body2" sx={{ fontSize: compact ? '0.7rem' : '0.875rem' }}>
                        {participantsText(event)}
                      </Typography>
                    </Box>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}
      </Paper>

      {/* Floating Action Button - only show for users who can create events */}
      {canCreateEvents && (
        <Fab
          color="primary"
          aria-label="Új esemény létrehozása"
          onClick={() => handleCreateEventClick()}
          sx={{
            position: 'fixed',
            bottom: isMobile ? 80 : 16, // Above mobile bottom navigation when on mobile
            right: 16,
            zIndex: 1000,
          }}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Dialogs */}
      <EventDetailsDialog
        open={eventDetailsOpen}
        eventId={selectedEventId}
        onClose={() => {
          setEventDetailsOpen(false);
          setSelectedEventId(null);
        }}
        onEdit={handleEditEvent}
        onDelete={handleDeleteEvent}
        onEventUpdated={handleEventUpdated}
        onNavigateToUserProfile={onNavigateToUserProfile}
        onNavigateToAttendance={onNavigateToAttendance}
      />

      <EventCreateDialog
        open={eventCreateOpen}
        onClose={() => {
          setEventCreateOpen(false);
          setCreateEventTime({ start: '', end: '' });
        }}
        onEventCreated={handleEventUpdated}
        initialDate={selectedDate}
        initialStartTime={createEventTime.start}
        initialEndTime={createEventTime.end}
      />

      <EventEditDialog
        open={eventEditOpen}
        eventId={selectedEventId}
        onClose={() => {
          setEventEditOpen(false);
          setSelectedEventId(null);
        }}
        onEventUpdated={handleEventUpdated}
      />
    </Box>
  );
};

export default DailyTimeline;